import { ChakraV3, QRCode, Spin } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedPinInput } from '../../../components/forms/HookedPinInput';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { type TotpEnrolChallenge, useAuth } from '../../../context/AuthContext';
import { sendExceptionToSentry } from '../../../utils/errors';
import { zodResolver } from '../../../utils/zod';
import { AuthCard } from './AuthCard';
import { AuthLayout } from './AuthLayout';
import { MFAChooseMethodButton } from './MFAChooseMethodButton';

type FormValues = {
  verificationCode: string;
};

const schema: z.ZodSchema<FormValues> = z.object({
  verificationCode: z.string().length(6).length(6, 'Verification code must be 6 characters'),
});

export const TotpEnrolCard = () => {
  const auth = useAuth();

  const [totpSecret, setTotpSecret] = React.useState<TotpEnrolChallenge | null>(null);
  const showSecretDisclosure = ChakraV3.useDisclosure();

  const methods = useForm<FormValues>({
    defaultValues: {
      verificationCode: '',
    },
    resolver: zodResolver(schema),
  });

  const setQrCode = React.useCallback(async () => {
    if (totpSecret) {
      return;
    }

    try {
      const totpSecret = await auth.generateTotpEnrolCode();
      setTotpSecret(totpSecret);
    } catch (err) {
      switch (err.code) {
        case 'auth/unverified-email':
          methods.setError('verificationCode', {
            message: 'Please verify your email before enrolling in MFA',
          });
          return;
        default:
          methods.setError('verificationCode', {
            message: 'Service temporarily unavailable. Please contact support',
          });
          return sendExceptionToSentry(err);
      }
    }
  }, [auth, totpSecret, methods.setError]);

  React.useEffect(() => {
    void setQrCode();
  }, [setQrCode]);

  const onVerifyCode = async (values: FormValues) => {
    if (!totpSecret) {
      return;
    }

    return auth.verifyTotpEnrolmentCode(totpSecret, values.verificationCode).catch(() => {
      methods.setError('verificationCode', {
        message: 'Invalid verification code',
      });
      methods.resetField('verificationCode', {
        keepError: true,
      });
    });
  };

  if (auth.isLoading) {
    return (
      <AuthLayout>
        <Spin />
      </AuthLayout>
    );
  }

  if (!auth.showEnrolMfa) {
    return <Navigate to='/login' />;
  }

  return (
    <AuthLayout>
      <AuthCard>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Text fontWeight='semibold' fontSize='2xl'>
            Secure Your Account
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Scan the QR Code below using your preferred authenticator app and then enter the
            provided one-time code below.
          </ChakraV3.Text>
        </ChakraV3.Stack>
        <ChakraV3.Stack gap={8}>
          <ChakraV3.Stack align='center' w='full' gap={2}>
            <ChakraV3.Stack h={48} w={48}>
              {totpSecret ? (
                <QRCode value={totpSecret.uri} />
              ) : (
                <ChakraV3.Skeleton w='full' h='full' />
              )}
            </ChakraV3.Stack>
            <ChakraV3.Stack gap={2} textAlign='center'>
              <ChakraV3.Collapsible.Root open={showSecretDisclosure.open}>
                <ChakraV3.Collapsible.Trigger>
                  <ChakraV3.Button
                    variant='plain'
                    onClick={showSecretDisclosure.onToggle}
                    size='xs'
                  >
                    Trouble scanning?
                  </ChakraV3.Button>
                </ChakraV3.Collapsible.Trigger>
                <ChakraV3.Collapsible.Content>
                  <ChakraV3.Text fontSize='xs' userSelect='text'>
                    <strong>Secret Key</strong>:{' '}
                    <span data-secret-key={totpSecret?.secret?.secretKey}>
                      {totpSecret?.secret?.secretKey}
                    </span>
                  </ChakraV3.Text>
                </ChakraV3.Collapsible.Content>
              </ChakraV3.Collapsible.Root>
            </ChakraV3.Stack>
          </ChakraV3.Stack>

          <FormProvider {...methods}>
            <ChakraV3.Stack as='form' gap={4} onSubmit={methods.handleSubmit(onVerifyCode)}>
              <HookedFormField name='verificationCode'>
                <ChakraV3.Stack align='center'>
                  <HookedPinInput name='verificationCode' fieldCount={6} />
                </ChakraV3.Stack>
              </HookedFormField>
              <HookedSubmitButton size='md' mt={4} disabled={false} loadingText='Verifying...'>
                Verify
              </HookedSubmitButton>
            </ChakraV3.Stack>
          </FormProvider>
        </ChakraV3.Stack>
        <MFAChooseMethodButton />
      </AuthCard>
    </AuthLayout>
  );
};
